import React, { useState, lazy, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout, getProfile } from '../../store/slices/authSlice';
import { Map, Compass, User, LogOut, Settings, Menu, X, MessageCircle, Users, Moon, Sun, ChevronDown, Rss, Shield, TreePine } from 'lucide-react';
import { selectTotalUnreadCount } from '../../store/slices/messagesSlice';
import { selectIsWebSocketConnected } from '../../store/slices/websocketSlice';
import { OptimizedAvatar } from '../OptimizedImage/OptimizedImage';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// Lazy load Messenger and NotificationCenter - они не критичны для начальной загрузки
const NotificationCenter = lazy(() => import('../NotificationCenter/NotificationCenter'));
const Messenger = lazy(() => import('../Messenger/Messenger'));

const Header = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  // Отслеживаем изменения avatarUrl для принудительного обновления
  const avatarUrl = user?.avatarUrl || user?.avatar_url;
  const isConnected = useSelector(selectIsWebSocketConnected);
  const totalUnreadCount = useSelector(selectTotalUnreadCount);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const device = useDeviceDetection();
  
  // Загружаем профиль при монтировании, если пользователь авторизован, но avatarUrl отсутствует
  // КРИТИЧНО: Используем useRef для отслеживания, был ли уже вызван getProfile(), чтобы избежать бесконечного цикла
  const getProfileCalledRef = React.useRef(false);
  const lastUserIdRef = React.useRef(null);
  
  React.useEffect(() => {
    // Если пользователь изменился (например, после логина), сбрасываем флаг
    if (user?.id !== lastUserIdRef.current) {
      getProfileCalledRef.current = false;
      lastUserIdRef.current = user?.id || null;
    }
    
    // Загружаем профиль, если:
    // 1. Пользователь авторизован
    // 2. Есть данные пользователя
    // 3. Аватар отсутствует
    // 4. getProfile() еще не был вызван для этого пользователя
    if (isAuthenticated && user && !user.avatarUrl && !user.avatar_url && !getProfileCalledRef.current) {
      // Помечаем, что getProfile() был вызван
      getProfileCalledRef.current = true;
      dispatch(getProfile()).catch((error) => {
        getProfileCalledRef.current = false; // Сбрасываем флаг при ошибке, чтобы можно было повторить
      });
    } else if (user && (user.avatarUrl || user.avatar_url)) {
      // Если avatarUrl появился, сбрасываем флаг (на случай, если пользователь загрузит аватар)
      getProfileCalledRef.current = false;
    }
  }, [isAuthenticated, user, dispatch]);
  const [activeLang, setActiveLang] = useState(() => localStorage.getItem('language') || i18n.language || 'ru');
  const [isMessengerOpen, setIsMessengerOpen] = useState(false);
  const [initialChatForMessenger, setInitialChatForMessenger] = useState(null);
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'forest' || saved === 'light') return saved;
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });
  const isDark = theme === 'dark';
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);
  const location = useLocation();

  // Apply theme to html element and theme-color meta (мобильная статусная строка/шапка браузера)
  React.useEffect(() => {
    const html = document.documentElement;
    html.classList.remove('dark', 'theme-forest');
    if (theme === 'dark') {
      html.classList.add('dark');
    } else if (theme === 'forest') {
      html.classList.add('theme-forest');
    }
    localStorage.setItem('theme', theme);
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      const color = theme === 'dark' ? '#111827' : theme === 'forest' ? '#2d3a2a' : '#ffffff';
      metaTheme.setAttribute('content', color);
    }
  }, [theme]);

  const cycleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : prev === 'dark' ? 'forest' : 'light'));
  };

  const themeTitle = theme === 'light' ? t('header.lightTheme') : theme === 'dark' ? t('header.darkTheme') : t('header.forestTheme');
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : TreePine;

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setIsMenuOpen(false);
  };

  // Close profile menu on outside click
  React.useEffect(() => {
    if (!isProfileMenuOpen) return;
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen]);

  // Close profile menu on route change
  React.useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  // Не закрываем мессенджер автоматически при переходе на профиль
  // Мессенджер должен закрываться только явно через кнопку закрытия

  // Listen external openMessenger events to open specific chat
  React.useEffect(() => {
    const handler = (e) => {
      const chat = e.detail?.chat || null;
      console.log('📩 Header: Received openMessenger event, chat:', chat);
      setInitialChatForMessenger(chat);
      setIsMessengerOpen(true);
    };
    window.addEventListener('openMessenger', handler);
    return () => window.removeEventListener('openMessenger', handler);
  }, []);

  // Listen for closeMessenger events
  React.useEffect(() => {
    const handler = () => {
      setIsMessengerOpen(false);
    };
    window.addEventListener('closeMessenger', handler);
    return () => window.removeEventListener('closeMessenger', handler);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setIsLanguageOpen(false);
  };

  const languages = [
    { code: 'en', name: 'EN' },
    { code: 'ru', name: 'RU' },
  ];

  return (
    <header className={`app-header shadow-lg sticky top-0 z-50 ${device.isMobile ? 'h-12' : 'h-14'}`} style={{ backgroundColor: 'var(--header-bg)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center ${device.isMobile ? 'h-12' : 'h-14'}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 transition-transform hover:scale-105">
            <Map className={`header-logo-icon ${device.isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-blue-600`} />
            {!device.isMobile && (
              <span className="header-logo-text text-xl font-bold text-gray-900 dark:text-gray-100">TravelMapPlatform</span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="header-desktop-nav hidden md:flex items-center space-x-8">
            <Link
              to="/map"
              className="header-nav-link text-gray-700 dark:text-gray-200 hover:text-blue-600 px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 border border-gray-300 dark:border-gray-600 hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200"
            >
              <Compass className="h-4 w-4" />
              <span>{t('navigation.map')}</span>
            </Link>
            <Link
              to="/feed"
              onMouseEnter={() => {
                // Preload FeedPage chunk при наведении
                import('../../pages/FeedPage/FeedPage');
              }}
              className="header-nav-link text-gray-700 dark:text-gray-200 hover:text-blue-600 px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 border border-gray-300 dark:border-gray-600 hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200"
            >
              <Rss className="h-4 w-4" />
              <span>{t('navigation.feed')}</span>
            </Link>
            <Link
              to="/groups"
              className="header-nav-link text-gray-700 dark:text-gray-200 hover:text-blue-600 px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 border border-gray-300 dark:border-gray-600 hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200"
            >
              <Users className="h-4 w-4" />
              <span>{t('navigation.groups')}</span>
            </Link>

            {/* Language switcher moved to right section */}

            {isAuthenticated ? (
              <div className="flex items-center space-x-4 relative">
                {/* Spacer for visual separation */}
                <div className="w-4" />
                {/* Theme toggle */}
                <button
                  onClick={cycleTheme}
                  className="theme-toggle-btn p-2 rounded-full text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-transform hover:scale-105"
                  title={themeTitle}
                  aria-label={themeTitle}
                >
                  <ThemeIcon className="h-5 w-5" />
                </button>

                

                {/* Notifications */}
                <div title={t('notifications.title')}>
                  <Suspense fallback={null}>
                    <NotificationCenter />
                  </Suspense>
                </div>

                {/* Messenger */}
                <button
                  onClick={() => setIsMessengerOpen(true)}
                  className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors transition-transform hover:scale-105"
                  aria-label={t('chat.title')}
                  title={t('chat.title')}
                >
                  <MessageCircle className="h-6 w-6" />
                  {totalUnreadCount > 0 && (
                    <span className="header-notification-badge absolute top-0 right-0 inline-flex items-center justify-center h-5 w-5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                    </span>
                  )}
                </button>

                {/* Profile dropdown */}
                <div className="relative flex items-center">
                  <div className="relative">
                    <Link 
                      to={`/profile/${user?.id || ''}`}
                      className="block transition-transform hover:scale-105"
                      onClick={(e) => {
                        // Если меню открыто, не переходим в профиль при клике на аватар
                        if (isProfileMenuOpen) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <OptimizedAvatar
                        key={`avatar-${user?.id}-${avatarUrl || 'no-avatar'}`}
                        src={avatarUrl}
                        alt={`${user?.firstName || user?.first_name || ''} ${user?.lastName || user?.last_name || ''}`.trim() || 'User'}
                        firstName={user?.firstName || user?.first_name}
                        lastName={user?.lastName || user?.last_name}
                        username={user?.username}
                        size={40}
                        className="rounded-full"
                        clickable={false}
                      />
                    </Link>
                    {/* Стрелочка в нижней правой части */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsProfileMenuOpen(v=>!v);
                      }} 
                      className="absolute -bottom-1 -right-1 header-avatar-badge bg-blue-600 dark:bg-blue-500 text-white rounded-full p-0.5 hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors shadow-md border-2 border-white dark:border-gray-900"
                      aria-label="Открыть меню профиля"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                {isProfileMenuOpen && (
                  <div ref={profileMenuRef} className="header-dropdown absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    {user?.isAdmin && (
                      <Link 
                        to="/admin" 
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {t('navigation.admin')}
                      </Link>
                    )}
                    <Link 
                      to={`/profile/${user?.id || ''}`} 
                      onClick={() => setIsProfileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {t('navigation.profileMenu')}
                    </Link>
                    <button 
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        handleLogout();
                      }} 
                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('navigation.logout')}</span>
                    </button>
                  </div>
                )}

                {/* Language Switcher (after profile menu) */}
                <div className="flex items-center space-x-2 ml-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setActiveLang(lang.code);
                        changeLanguage(lang.code);
                      }}
                      className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                        activeLang === lang.code
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 header-lang-btn-active'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 header-lang-btn-inactive'
                      }`}
                      title={lang.name}
                      aria-label={`Language ${lang.name}`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
                <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="header-mobile-link text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/register"
                  className="header-btn-primary bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
                  data-header-register-button
                >
                  {t('navigation.register')}
                </Link>
                {/* Theme toggle for guests */}
                <button
                  onClick={cycleTheme}
                  className="theme-toggle-btn p-2 rounded-full text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-transform hover:scale-105"
                  title={themeTitle}
                  aria-label={themeTitle}
                >
                  <ThemeIcon className="h-5 w-5" />
                </button>

                {/* Language Switcher (right side for guests) */}
                <div className="flex items-center space-x-2 ml-1" translate="no" aria-label="Language selector">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setActiveLang(lang.code);
                        changeLanguage(lang.code);
                      }}
                      className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                        activeLang === lang.code
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 header-lang-btn-active'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 header-lang-btn-inactive'
                      }`}
                      title={lang.name}
                      aria-label={`Language ${lang.name}`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Language selector moved to profile dropdown */}
          </nav>

          {/* Mobile menu button - упрощенный на мобильных, так как есть bottom navigation */}
          <div className="header-mobile-nav md:hidden flex items-center space-x-2">
            {/* На мобильных показываем только важные элементы */}
            {isAuthenticated ? (
              <>
                {/* Notifications на мобильных */}
                <div title={t('notifications.title')}>
                  <Suspense fallback={null}>
                    <NotificationCenter />
                  </Suspense>
                </div>
                
                {/* Messenger на мобильных */}
                <button
                  onClick={() => setIsMessengerOpen(true)}
                  className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors"
                  aria-label={t('chat.title')}
                  title={t('chat.title')}
                >
                  <MessageCircle className="h-5 w-5" />
                  {totalUnreadCount > 0 && (
                    <span className="header-notification-badge absolute top-0 right-0 inline-flex items-center justify-center h-4 w-4 text-xs font-semibold text-white bg-blue-600 rounded-full">
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                    </span>
                  )}
                </button>
              </>
            ) : (
              <>
                {/* Кнопки логина и регистрации для неавторизованных пользователей на мобилке */}
                <Link
                  to="/login"
                  className="header-mobile-link px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/register"
                  className="header-btn-primary px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  data-header-register-button
                >
                  {t('navigation.register')}
                </Link>
              </>
            )}
            
            {/* Theme toggle на мобильных */}
            <button
              onClick={cycleTheme}
              className="theme-toggle-btn p-2 rounded-full text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={themeTitle}
              aria-label={themeTitle}
            >
              <ThemeIcon className="h-5 w-5" />
            </button>

            {/* Language selector for mobile */}
            <div className="flex items-center space-x-1" translate="no" aria-label="Language selector">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setActiveLang(lang.code);
                    changeLanguage(lang.code);
                  }}
                  className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${
                    activeLang === lang.code
                      ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 header-lang-btn-active'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 header-lang-btn-inactive'
                  }`}
                  title={lang.name}
                  aria-label={`Language ${lang.name}`}
                >
                  {lang.name}
                </button>
              ))}
            </div>

            {/* Profile menu на мобильных */}
            {isAuthenticated && (
              <>
                {user?.isAdmin && (
                  <Link 
                    to="/admin"
                    className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                    title={t('navigation.admin')}
                  >
                    <Shield className="h-5 w-5" />
                  </Link>
                )}
                <div className="relative">
                  <Link 
                    to={`/profile/${user?.id || ''}`}
                    className="block"
                  >
                    <OptimizedAvatar
                    key={`avatar-mobile-${user?.id}-${avatarUrl || 'no-avatar'}`}
                    src={avatarUrl}
                    alt={`${user?.firstName || user?.first_name || ''} ${user?.lastName || user?.last_name || ''}`.trim() || 'User'}
                    firstName={user?.firstName || user?.first_name}
                    lastName={user?.lastName || user?.last_name}
                    username={user?.username}
                    size={32}
                    className="rounded-full"
                    clickable={false}
                  />
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile Navigation - скрыто, так как используется bottom navigation */}
        {/* Оставляем только для неавторизованных пользователей или для дополнительных пунктов */}
        {isMenuOpen && !device.isMobile && (
          <div className="header-mobile-menu md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {!isAuthenticated && (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-gray-700 dark:text-gray-200 hover:text-blue-600 rounded-md text-base font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('navigation.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 bg-blue-600 text-white rounded-md text-base font-medium hover:bg-blue-700"
                    onClick={() => setIsMenuOpen(false)}
                    data-header-register-button
                  >
                    {t('navigation.register')}
                  </Link>
                </>
              )}
              {isAuthenticated && user?.isAdmin && (
                <Link
                  to="/admin"
                  className="block px-3 py-2 text-gray-700 dark:text-gray-200 hover:text-blue-600 rounded-md text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.admin')}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Messenger */}
      {isMessengerOpen && (
        <Suspense fallback={null}>
          <Messenger 
            isOpen={isMessengerOpen} 
            onClose={() => setIsMessengerOpen(false)} 
            initialChat={initialChatForMessenger}
          />
        </Suspense>
      )}
    </header>
  );
};

export default Header;